const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8001/api/v1';

// Backend origin without the /api/v1 suffix — used to build URLs for static assets
// (e.g. generated lesson images served from /static).
export const STATIC_BASE_URL = API_BASE_URL.replace(/\/api\/v\d+\/?$/, '');

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

// Single-flight guard: when several requests get a 401 at once (e.g. the results
// page firing record-attempt + save-progress together), they must share
// ONE refresh. Otherwise each rotates the refresh token and the later ones send a
// now-stale token, fail, and bounce the user to /login mid-session.
let refreshPromise = null;

// Performs the actual token refresh. Always go through refreshAccessToken().
const doRefreshAccessToken = async () => {
  const refreshToken = localStorage.getItem('refresh_token');

  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  try {
    const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refresh_token: refreshToken })
    });

    if (!response.ok) {
      // If refresh fails, clear tokens and redirect to login
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      window.location.href = '/login'; // Redirect to login page
      throw new Error('Token refresh failed');
    }

    const data = await response.json();

    // Update both tokens in localStorage
    localStorage.setItem('access_token', data.access_token);
    localStorage.setItem('refresh_token', data.refresh_token);

    return data.access_token;
  } catch (error) {
    console.error('Error refreshing token:', error);
    throw error;
  }
};

// Function to refresh access token — de-duplicates concurrent callers so only one
// refresh request is ever in flight at a time.
const refreshAccessToken = async () => {
  if (refreshPromise) {
    return refreshPromise;
  }
  refreshPromise = doRefreshAccessToken().finally(() => {
    refreshPromise = null;
  });
  return refreshPromise;
};

// Překlady nejčastějších validačních chyb z backendu (FastAPI/Pydantic) do češtiny.
const VALIDATION_MESSAGES = {
  email: 'Zadej platnou e-mailovou adresu.',
  password: 'Heslo není platné.',
  username: 'Uživatelské jméno není platné.',
};

// Backend vrací `detail` buď jako text (HTTPException), nebo jako seznam objektů
// (chyba validace 422). Seznam by se v hlášce vypsal jako "[object Object]",
// proto ho tu převedeme na čitelnou větu.
const detailToMessage = (detail) => {
  if (detail == null) return null;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const messages = detail.map((item) => {
      if (typeof item === 'string') return item;
      const field = Array.isArray(item?.loc) ? item.loc[item.loc.length - 1] : null;
      if (field && VALIDATION_MESSAGES[field]) return VALIDATION_MESSAGES[field];
      const msg = item?.msg || 'Neplatná hodnota.';
      return field ? `${field}: ${msg}` : msg;
    });
    return [...new Set(messages)].join(' ');
  }
  if (typeof detail === 'object' && detail.msg) return detail.msg;
  return String(detail);
};

// Wrapper function for API requests with automatic token refresh
const apiRequest = async (endpoint, options = {}) => {
  // Check if user has access token
  const token = localStorage.getItem('access_token');

  // If no token exists, redirect to login immediately
  if (!token) {
    console.warn('No access token found, redirecting to login');
    window.location.href = '/login';
    throw new Error('Authentication required');
  }

  let response = await fetch(endpoint, {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options.headers
    }
  });

  // If the request fails with 401 Unauthorized, try to refresh the token
  if (response.status === 401) {
    try {
      const newAccessToken = await refreshAccessToken();

      // Retry the original request with the new token
      response = await fetch(endpoint, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
          'Authorization': `Bearer ${newAccessToken}`
        }
      });
    } catch (refreshError) {
      // If token refresh fails, user is already redirected in refreshAccessToken
      // Just throw the error to stop execution
      throw refreshError;
    }
  }

  if (!response.ok) {
    let detail = null;
    try {
      const body = await response.json();
      detail = detailToMessage(body.detail);
    } catch { /* ignore parse errors */ }
    throw new Error(detail || `HTTP error! status: ${response.status}`);
  }

  return response;
};

// Chat API Functions
export const fetchPublicMessages = async (limit = 50) => {
  const response = await apiRequest(`${API_BASE_URL}/chat/public/messages?limit=${limit}`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const postPublicMessage = async (content) => {
  const response = await apiRequest(`${API_BASE_URL}/chat/public/messages`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ content })
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const fetchChatContacts = async () => {
  const response = await apiRequest(`${API_BASE_URL}/chat/contacts`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const fetchPrivateMessages = async (contactId, limit = 50) => {
  const response = await apiRequest(`${API_BASE_URL}/chat/private/messages/${contactId}?limit=${limit}`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const postPrivateMessage = async (contactId, content) => {
  const response = await apiRequest(`${API_BASE_URL}/chat/private/messages/${contactId}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ content })
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

// Diary API Functions
export const fetchDiaryEntry = async (topic, noteType, exerciseType = null) => {
  const params = new URLSearchParams({ topic, note_type: noteType });
  if (exerciseType) {
    params.append('exercise_type', exerciseType);
  }

  const response = await apiRequest(`${API_BASE_URL}/diary/entries?${params.toString()}`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const saveDiaryEntry = async ({ topic, noteType, exerciseType = null, note, questionnaire = null }) => {
  const response = await apiRequest(`${API_BASE_URL}/diary/entries`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      topic,
      note_type: noteType,
      exercise_type: exerciseType,
      note,
      questionnaire
    })
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

// Function to fetch audio with authentication
export const fetchAudio = async (questionText, speaker = "am_michael") => {
  const audioUrl = `${API_BASE_URL}/audio/speaking`;

  const response = await apiRequest(audioUrl, {
    method: 'POST',
    body: JSON.stringify({
        text: questionText,
        speaker: speaker
    }),
  });

  if (!response.ok) {
    throw new Error(`Audio fetch failed with status: ${response.status}`);
  }

  return response.blob();
};

/**
 * Fetch audio with word-level timing data for karaoke highlighting.
 * @param {string} questionText - Text to convert to speech
 * @param {string} speaker - Voice ID (default: am_michael)
 * @returns {Promise<{audioBlob: Blob, wordTimings: Array, duration: number}>}
 */
export const fetchAudioWithTimings = async (questionText, speaker = "am_michael") => {
  const audioUrl = `${API_BASE_URL}/audio/speaking-with-timings`;

  const response = await apiRequest(audioUrl, {
    method: 'POST',
    body: JSON.stringify({
      text: questionText,
      speaker: speaker
    }),
  });

  if (!response.ok) {
    throw new Error(`Audio fetch with timings failed with status: ${response.status}`);
  }

  const data = await response.json();

  // Convert base64 audio to blob
  const binaryString = atob(data.audio_data);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  const audioBlob = new Blob([bytes], { type: 'audio/wav' });

  return {
    audioBlob,
    wordTimings: data.word_timings,
    duration: data.duration
  };
};

/**
 * Assess pronunciation of a recorded sentence.
 * @param {Blob} audioBlob - WAV recording (16 kHz mono PCM) of the learner.
 * @param {string} referenceText - The sentence the learner was asked to say.
 *   Empty = unscripted mode: Azure transcribes free speech and scores it
 *   without a reference (used by the AI voice conversation).
 * @returns {Promise<{recognized_text, accuracy, fluency, completeness, pronunciation, words}>}
 */
export const assessPronunciation = async (audioBlob, referenceText = '') => {
  // Convert the WAV blob to base64 (chunked to avoid call-stack limits on big buffers).
  const arrayBuffer = await audioBlob.arrayBuffer();
  const bytes = new Uint8Array(arrayBuffer);
  let binary = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunkSize));
  }
  const audioBase64 = btoa(binary);

  const response = await apiRequest(`${API_BASE_URL}/audio/assess-pronunciation`, {
    method: 'POST',
    body: JSON.stringify({
      audio_data: audioBase64,
      reference_text: referenceText
    }),
  });

  return response.json();
};

export const fetchConversationExercise = async (params) => {
  const { topic, dialogueNumber = 1 } = params;
  const query_params = new URLSearchParams({
    topic,
    dialogue_number: dialogueNumber,
  });

  const response = await apiRequest(`${API_BASE_URL}/exercises/conversation?${query_params}`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();

  // Transform API response to match expected format
  return {
    conversationId: data.conversation_id ?? null,
    dialogueNumber: data.dialogue_number ?? dialogueNumber,
    dialogue: data.dialogue || [],
    questions: (data.question_answers || []).map(qa => ({
      id: qa.question_number,
      qa_id: qa.qa_id,
      question: qa.question,
      answer: qa.answer,
      alternative_answers: qa.alternative_answers || []
    }))
  };
};

export const fetchConversationFillInExercise = async (params) => {
  const { topic } = params;
  const query_params = new URLSearchParams({ topic });

  const response = await apiRequest(`${API_BASE_URL}/exercises/conversation-fillin?${query_params}`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();

  return {
    questions: (data.questions || []).map(q => ({
      id: q.id,
      question: q.question,
      partial_answer: q.partial_answer,
      answer: q.answer,
      full_answer: q.full_answer
    })),
    word_options: data.word_options || []
  };
};

export const fetchVocabularyFillInExercise = async (params) => {
  const { topic, conversationId } = params;
  const query_params = new URLSearchParams({ topic });
  if (conversationId != null) {
    query_params.set('conversation_id', String(conversationId));
  }

  const response = await apiRequest(`${API_BASE_URL}/exercises/vocabulary-fillin?${query_params}`, {
    method: 'GET',
  });


  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();
  
  // Transform API response to match expected format
  return {
    exercises: (data.exercises || []).map(exercise => ({
      id: exercise.id,
      sentence_before: exercise.sentence_before,
      sentence_after: exercise.sentence_after,
      hint: exercise.hint,
      correct_answer: exercise.correct_answer,
      options: exercise.options
    }))
  };
};

export const getVocabularyExplanation = async (exerciseId, wrongAnswer = null) => {
  const query = wrongAnswer
    ? `?wrong_answer=${encodeURIComponent(wrongAnswer)}`
    : '';
  const response = await apiRequest(
    `${API_BASE_URL}/exercises/vocabulary-fillin/${exerciseId}/explanation${query}`,
    { method: 'GET' }
  );

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const getConversationFillinExplanation = async (sentence, word, wrongAnswer = null) => {
  const params = new URLSearchParams({ sentence, word });
  if (wrongAnswer) {
    params.append('wrong_answer', wrongAnswer);
  }
  const response = await apiRequest(
    `${API_BASE_URL}/exercises/conversation-fillin/explanation?${params}`,
    { method: 'GET' }
  );

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const fetchSpeakingExercise = async (params) => {
  const { topic } = params;
  const query_params = new URLSearchParams({
    topic
  });

  const response = await apiRequest(`${API_BASE_URL}/exercises/speaking?${query_params}`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();

  // Transform API response to match expected format
  return {
    instruction: data.instruction || '',
    questions: (data.questions || []).map(q => ({
      id: q.id,
      question: q.question,
      hint: q.hint
    }))
  };
};

export const evaluateConversationWritingExercise = async (exerciseContext, question, userAnswer) => {
  const response = await apiRequest(`${API_BASE_URL}/exercises/evaluate-conversation-writing`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      exercise: exerciseContext,
      question_id: question.id,
      question: question.question,
      user_answer: userAnswer,
      correct_answer: question.alternative_answers?.length > 0
        ? `${question.answer} (Další možné odpovědi: ${question.alternative_answers.join('; ')})`
        : question.answer
    })
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();

  return {
    question_id: data.question_id,
    evaluation: data.evaluation,
    is_correct: data.is_correct
  };
};

// Progress Tracking API Functions

export const recordExerciseAttempt = async (attemptData) => {
  const response = await apiRequest(`${API_BASE_URL}/progress/attempt`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(attemptData)
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const getTimeStatistics = async () => {
  const response = await apiRequest(`${API_BASE_URL}/progress/time-stats`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const getProgressOverview = async () => {
  const response = await apiRequest(`${API_BASE_URL}/progress/overview`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const getGrammarCategoryStats = async () => {
  const response = await apiRequest(`${API_BASE_URL}/progress/grammar-stats`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

// Data pro domovskou sekci „Co dnes": co dnes zopakovat + kde student naposledy skončil.
export const fetchToday = async () => {
  const response = await apiRequest(`${API_BASE_URL}/progress/today`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

// Exercise Progress API Functions

const progressEndpoint = (topic, exerciseType, conversationId) => {
  const base = `${API_BASE_URL}/progress/exercise/${encodeURIComponent(topic)}/${encodeURIComponent(exerciseType)}`;
  return conversationId != null ? `${base}?conversation_id=${conversationId}` : base;
};

export const saveExerciseProgress = async (topic, exerciseType, progressData, conversationId = null) => {
  const body = {
    topic,
    exercise_type: exerciseType,
    progress_data: progressData,
  };
  if (conversationId != null) {
    body.conversation_id = conversationId;
  }

  const response = await apiRequest(`${API_BASE_URL}/progress/save-exercise`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body)
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const loadExerciseProgress = async (topic, exerciseType, conversationId = null) => {
  const response = await apiRequest(progressEndpoint(topic, exerciseType, conversationId), {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const resetExerciseProgress = async (topic, exerciseType, conversationId = null) => {
  const response = await apiRequest(progressEndpoint(topic, exerciseType, conversationId), {
    method: 'DELETE',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const getAllTopicsExercisesStatus = async () => {
  const response = await apiRequest(`${API_BASE_URL}/progress/topics/exercises-status`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

// Admin Exercise Update API Functions

export const updateVocabularyFillinExercise = async (exerciseId, updateData, topic) => {
  const url = `${API_BASE_URL}/exercises/update/vocabulary-fillin/${exerciseId}`;
  const params = topic ? `?topic=${encodeURIComponent(topic)}` : '';

  const response = await apiRequest(url + params, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(updateData)
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const updateSpeakingExercise = async (exerciseId, updateData, topic) => {
  const url = `${API_BASE_URL}/exercises/update/speaking/${exerciseId}`;
  const params = topic ? `?topic=${encodeURIComponent(topic)}` : '';

  const response = await apiRequest(url + params, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(updateData)
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

// Fetch all exercises for admin management
export const fetchAllVocabularyFillinExercises = async (topic = null) => {
  const url = `${API_BASE_URL}/admin/exercises/vocabulary-fillin`;
  const params = topic ? `?topic=${encodeURIComponent(topic)}` : '';

  const response = await apiRequest(url + params, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const fetchAllSpeakingExercises = async (topic = null) => {
  const url = `${API_BASE_URL}/admin/exercises/speaking`;
  const params = topic ? `?topic=${encodeURIComponent(topic)}` : '';

  const response = await apiRequest(url + params, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const fetchAdminStats = async () => {
  const url = `${API_BASE_URL}/admin/stats`;

  const response = await apiRequest(url, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const fetchDAUStats = async (days = 7) => {
  const response = await apiRequest(`${API_BASE_URL}/admin/analytics/dau?days=${days}`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const fetchAdminDiaryEntries = async () => {
  const response = await apiRequest(`${API_BASE_URL}/admin/diary-entries`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

// Create exercise functions
export const createVocabularyFillinExercise = async (data) => {
  const response = await apiRequest(`${API_BASE_URL}/admin/exercises/vocabulary-fillin`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const createSpeakingExercise = async (data) => {
  const response = await apiRequest(`${API_BASE_URL}/admin/exercises/speaking`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

// Delete exercise functions
export const deleteVocabularyFillinExercise = async (exerciseId) => {
  const response = await apiRequest(`${API_BASE_URL}/admin/exercises/vocabulary-fillin/${exerciseId}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const deleteSpeakingExercise = async (exerciseId) => {
  const response = await apiRequest(`${API_BASE_URL}/admin/exercises/speaking/${exerciseId}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

// AI conversation scenarios admin API

export const fetchAllAiScenarios = async (topic = null) => {
  const params = topic ? `?topic=${encodeURIComponent(topic)}` : '';
  const response = await apiRequest(`${API_BASE_URL}/admin/exercises/ai-conversation${params}`, {
    method: 'GET',
  });
  return await response.json();
};

export const createAiScenario = async (data) => {
  const response = await apiRequest(`${API_BASE_URL}/admin/exercises/ai-conversation`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  return await response.json();
};

export const updateAiScenario = async (scenarioId, data) => {
  const response = await apiRequest(`${API_BASE_URL}/admin/exercises/ai-conversation/${scenarioId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  return await response.json();
};

export const deleteAiScenario = async (scenarioId) => {
  const response = await apiRequest(`${API_BASE_URL}/admin/exercises/ai-conversation/${scenarioId}`, {
    method: 'DELETE',
  });
  return await response.json();
};

// Writing exercises admin API

export const fetchAllWritingExercises = async (topic = null) => {
  const url = `${API_BASE_URL}/admin/exercises/writing`;
  const params = topic ? `?topic=${encodeURIComponent(topic)}` : '';

  const response = await apiRequest(url + params, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const createWritingExercise = async (data) => {
  const response = await apiRequest(`${API_BASE_URL}/admin/exercises/writing`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const updateWritingExercise = async (exerciseId, updateData) => {
  const response = await apiRequest(`${API_BASE_URL}/exercises/update/writing/${exerciseId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updateData)
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const deleteWritingExercise = async (exerciseId) => {
  const response = await apiRequest(`${API_BASE_URL}/admin/exercises/writing/${exerciseId}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

// Topic Fill-in Q&A (Doplňování) admin API

const DUPLICATE_QA_ORDER_MESSAGE = 'Pořadí (qa_order) je v tomto tématu už obsazené. Zvol jiné číslo.';

export const fetchAllTopicFillinQA = async (topic = null) => {
  const url = `${API_BASE_URL}/admin/exercises/topic-fillin-qa`;
  const params = topic ? `?topic=${encodeURIComponent(topic)}` : '';

  const response = await apiRequest(url + params, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const createTopicFillinQA = async (data) => {
  const response = await apiRequest(`${API_BASE_URL}/admin/exercises/topic-fillin-qa`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    if (response.status === 409) {
      throw new Error(DUPLICATE_QA_ORDER_MESSAGE);
    }
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const updateTopicFillinQA = async (qaId, updateData) => {
  const response = await apiRequest(`${API_BASE_URL}/exercises/update/topic-fillin-qa/${qaId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updateData)
  });

  if (!response.ok) {
    if (response.status === 409) {
      throw new Error(DUPLICATE_QA_ORDER_MESSAGE);
    }
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const deleteTopicFillinQA = async (qaId) => {
  const response = await apiRequest(`${API_BASE_URL}/admin/exercises/topic-fillin-qa/${qaId}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

// Dynamic Lessons (user-generated conversations) API
export const generateDynamicLesson = async (topic) => {
  const response = await apiRequest(`${API_BASE_URL}/exercises/conversation/generate`, {
    method: 'POST',
    body: JSON.stringify({ topic })
  });
  return await response.json();
};

export const fetchDynamicLessons = async () => {
  const response = await apiRequest(`${API_BASE_URL}/exercises/conversation/dynamic`, {
    method: 'GET',
  });
  return await response.json();
};

export const deleteDynamicLesson = async (conversationId) => {
  const response = await apiRequest(`${API_BASE_URL}/exercises/conversation/dynamic/${conversationId}`, {
    method: 'DELETE',
  });
  return await response.json();
};

// Lesson visibility — admin může lekce schovat z menu běžných uživatelů.
export const fetchHiddenLessons = async () => {
  const response = await apiRequest(`${API_BASE_URL}/lessons/hidden`, {
    method: 'GET',
  });
  return await response.json();
};

export const setLessonHidden = async (topic, hidden) => {
  const response = await apiRequest(`${API_BASE_URL}/lessons/${encodeURIComponent(topic)}/visibility`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ hidden }),
  });
  return await response.json();
};

// Conversation CRUD functions
export const fetchAllConversations = async (topic = null) => {
  const url = `${API_BASE_URL}/admin/exercises/conversations`;
  const params = topic ? `?topic=${encodeURIComponent(topic)}` : '';

  const response = await apiRequest(url + params, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const createConversation = async (data) => {
  const response = await apiRequest(`${API_BASE_URL}/admin/exercises/conversations`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const updateConversation = async (conversationId, updateData, topic) => {
  const url = `${API_BASE_URL}/exercises/update/conversation/${conversationId}`;
  const params = topic ? `?topic=${encodeURIComponent(topic)}` : '';

  const response = await apiRequest(url + params, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updateData)
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const deleteConversation = async (conversationId) => {
  const response = await apiRequest(`${API_BASE_URL}/admin/exercises/conversations/${conversationId}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const updateConversationQA = async (qaId, updateData) => {
  const url = `${API_BASE_URL}/exercises/update/conversation-qa/${qaId}`;

  const response = await apiRequest(url, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updateData)
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

// Vocabulary Dictionary API Functions

export const fetchDictionaryWords = async () => {
  const response = await apiRequest(`${API_BASE_URL}/dictionary/words`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const fetchDictionaryWordAudio = async (word) => {
  const response = await apiRequest(`${API_BASE_URL}/dictionary/audio/${encodeURIComponent(word)}`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.blob();
};

// User Vocabulary API Functions

export const fetchUserVocabulary = async (topic = null) => {
  const url = `${API_BASE_URL}/user-vocabulary/words`;
  const params = topic ? `?topic=${encodeURIComponent(topic)}` : '';

  const response = await apiRequest(url + params, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const addUserVocabularyWord = async (data) => {
  const response = await apiRequest(`${API_BASE_URL}/user-vocabulary/words`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const updateUserVocabularyWord = async (id, data) => {
  const response = await apiRequest(`${API_BASE_URL}/user-vocabulary/words/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const deleteUserVocabularyWord = async (id) => {
  const response = await apiRequest(`${API_BASE_URL}/user-vocabulary/words/${id}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
};

export const generateDictionaryAudio = async (ids) => {
  const response = await apiRequest(`${API_BASE_URL}/admin/vocabulary-dictionary/generate-audio`, {
    method: 'POST',
    body: JSON.stringify({ ids }),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const generateSpeakingAudio = async (ids) => {
  const response = await apiRequest(`${API_BASE_URL}/admin/speaking-exercises/generate-audio`, {
    method: 'POST',
    body: JSON.stringify({ ids }),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const generateAllConversationAudio = async () => {
  const response = await apiRequest(`${API_BASE_URL}/admin/conversations/generate-audio`, {
    method: 'POST',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const generateConversationAudio = async (conversationId) => {
  const response = await apiRequest(`${API_BASE_URL}/admin/conversations/${conversationId}/generate-audio`, {
    method: 'POST',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

// Writing Exercise API Functions

export const fetchWritingExercise = async (params) => {
  const { topic } = params;
  const query_params = new URLSearchParams({
    topic
  });

  const response = await apiRequest(`${API_BASE_URL}/exercises/writing?${query_params}`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();

  return {
    id: data.id,
    title: data.title,
    instruction: data.instruction,
    min_sentences: data.min_sentences,
    guiding_questions: data.guiding_questions || [],
    examples: data.examples || []
  };
};

export const evaluateWritingSentence = async (data) => {
  const response = await apiRequest(`${API_BASE_URL}/exercises/evaluate-writing-sentence`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

// BBB
export const joinMeeting = async (meetingId, userName, role) => {
  const response = await apiRequest(`${API_BASE_URL}/bbb/join`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      meeting_id: meetingId,
      user_name: userName,
      role
    })
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || 'Failed to join meeting');
  }

  const data = await response.json();
  return data.joinUrl;

};

export const askAiTutor = async (messages) => {
  const response = await apiRequest(`${API_BASE_URL}/ai-tutor/ask`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ messages }),
  });
  return await response.json();
};

// --- Spoken AI conversation ---

/** Scenarios available for a topic (id, difficulty, opening_message — the AI role stays hidden). */
export const fetchAiConversationScenarios = async (topic) => {
  const params = topic ? `?topic=${encodeURIComponent(topic)}` : '';
  const response = await apiRequest(`${API_BASE_URL}/ai-conversation/scenarios${params}`, {
    method: 'GET',
  });
  const data = await response.json();
  return data.scenarios;
};

/**
 * Get the AI partner's next reply for the conversation so far.
 * @param {number} scenarioId
 * @param {Array<{role: string, content: string}>} messages - history ending with the learner's message
 * @returns {Promise<{reply: string, wrapping_up: boolean}>}
 */
export const aiConversationReply = async (scenarioId, messages) => {
  const response = await apiRequest(`${API_BASE_URL}/ai-conversation/reply`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ scenario_id: scenarioId, messages }),
  });
  return await response.json();
};

/**
 * Czech wrap-up of the finished conversation (pronunciation + language feedback).
 * @param {number} scenarioId
 * @param {Array<{text: string, problem_words: Array}>} turns - the learner's turns
 * @returns {Promise<{summary: string}>}
 */
export const aiConversationSummary = async (scenarioId, turns) => {
  const response = await apiRequest(`${API_BASE_URL}/ai-conversation/summary`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ scenario_id: scenarioId, turns }),
  });
  return await response.json();
};

export const translateWord = async (word) => {
  const response = await apiRequest(`${API_BASE_URL}/translate/word`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ word }),
  });

  if (!response.ok) {
    throw new Error(`Translation failed: ${response.status}`);
  }

  return await response.json();
};

export const fetchGlobalDictionary = async () => {
  const response = await apiRequest(`${API_BASE_URL}/admin/global-dictionary`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch global dictionary: ${response.status}`);
  }

  return await response.json();
};

export const createGlobalDictionaryEntry = async (data) => {
  const response = await apiRequest(`${API_BASE_URL}/admin/global-dictionary`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || `Failed to create entry: ${response.status}`);
  }

  return await response.json();
};

export const updateGlobalDictionaryEntry = async (entryId, data) => {
  const response = await apiRequest(`${API_BASE_URL}/admin/global-dictionary/${entryId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || `Failed to update entry: ${response.status}`);
  }

  return await response.json();
};

export const deleteGlobalDictionaryEntry = async (entryId) => {
  const response = await apiRequest(`${API_BASE_URL}/admin/global-dictionary/${entryId}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    throw new Error(`Failed to delete entry: ${response.status}`);
  }

  return await response.json();
};

export const fetchAdminUsers = async () => {
  const response = await apiRequest(`${API_BASE_URL}/auth/users`);
  if (!response.ok) {
    throw new Error(`Failed to fetch users: ${response.status}`);
  }
  return await response.json();
};

export const updateAdminUser = async (userId, data) => {
  const response = await apiRequest(`${API_BASE_URL}/auth/users/${userId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    throw new Error(`Failed to update user: ${response.status}`);
  }
  return await response.json();
};

export const createAdminUser = async (data) => {
  const response = await apiRequest(`${API_BASE_URL}/auth/users`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    // Backend vrací srozumitelný detail ("Username/Email already registered").
    let detail = null;
    try {
      const body = await response.json();
      detail = body?.detail;
    } catch (e) { /* tělo nemusí být JSON */ }
    throw new Error(detail || `Failed to create user: ${response.status}`);
  }
  return await response.json();
};

export const deleteAdminUser = async (userId) => {
  const response = await apiRequest(`${API_BASE_URL}/auth/users/${userId}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    let detail = null;
    try {
      const body = await response.json();
      detail = body?.detail;
    } catch (e) { /* tělo nemusí být JSON */ }
    throw new Error(detail || `Failed to delete user: ${response.status}`);
  }
  // DELETE vrací 204 bez těla — žádné response.json().
};

export const fetchAdminStudentsProgress = async () => {
  const response = await apiRequest(`${API_BASE_URL}/admin/analytics/students`, {
    method: 'GET',
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch students progress: ${response.status}`);
  }
  return await response.json();
};

export const fetchAdminStudentDetail = async (userId, minAttempts = 1) => {
  const response = await apiRequest(
    `${API_BASE_URL}/admin/analytics/students/${userId}?min_attempts=${minAttempts}`,
    { method: 'GET' }
  );
  if (!response.ok) {
    throw new Error(`Failed to fetch student detail: ${response.status}`);
  }
  return await response.json();
};

export const fetchAdminUserVocabulary = async (userId) => {
  const response = await apiRequest(`${API_BASE_URL}/admin/users/${userId}/vocabulary`, {
    method: 'GET',
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch user vocabulary: ${response.status}`);
  }
  return await response.json();
};

export const fetchPurgePreview = async () => {
  const response = await apiRequest(`${API_BASE_URL}/admin/purge-exercises/preview`, {
    method: 'GET',
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return await response.json();
};

export const purgeExercises = async (exerciseTypes, purgeUserProgress = false) => {
  const response = await apiRequest(`${API_BASE_URL}/admin/purge-exercises`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      exercise_types: exerciseTypes,
      purge_user_progress: purgeUserProgress,
    }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || `Purge failed: ${response.status}`);
  }
  return await response.json();
};
